// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";

/**
 * Script start
 */

@Component({
  selector: "app-view-detail-dialog",
  templateUrl: "./view-detail-dialog.component.html",
  styleUrls: ["./view-detail-dialog.component.scss"],
})
export class BalanceViewDetailDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit phonebook contact form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Edit phonebook contact form

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<BalanceViewDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    public confirmDialog: MatDialog
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
   ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    if (this.data) {
      this.model.detail = this.data.detail;

      this.fields = [
        {
          key: "detail",
          type: "repeat",
          fieldArray: {
            fieldGroupClassName: "row",
            fieldGroup: [
              {
                className: "col-12 col-md-4 col-lg-2",
                type: "input",
                key: "code",
                id: "111",
                templateOptions: {
                  label: this.translateService.instant(
                    "balances.fields.detail.code"
                  ),
                  placeholder: this.translateService.instant(
                    "balances.fields.detail.code"
                  ),
                  disabled: true,
                },
              },
              {
                className: "col-12 col-md-4 col-lg-4",
                type: "textarea",
                key: "description",
                templateOptions: {
                  label: this.translateService.instant(
                    "balances.fields.detail.description"
                  ),
                  placeholder: this.translateService.instant(
                    "balances.fields.detail.description"
                  ),
                  disabled: true,
                },
                expressionProperties: {
                  "templateOptions.rows": (model) => {
                    return model.description.length
                      ? Math.ceil(model.description.length / 50)
                      : 1; // 50 is the average char number in one line
                  },
                },
              },
              {
                className: "col-12 col-md-4 col-lg-1",
                type: "input",
                key: "quantity",
                templateOptions: {
                  type: "number",
                  label: this.translateService.instant(
                    "balances.fields.detail.quantity"
                  ),
                  placeholder: this.translateService.instant(
                    "balances.fields.detail.quantity"
                  ),
                  min: 0,
                  required: true,
                  disabled: true,
                },
              },
              {
                className: "col-12 col-md-4 col-lg-2",
                type: "input",
                key: "unitPrice",
                templateOptions: {
                  type: "number",
                  label: this.translateService.instant(
                    "balances.fields.detail.unitPrice"
                  ),
                  placeholder: this.translateService.instant(
                    "balances.fields.detail.unitPrice"
                  ),
                  min: 0,
                  required: true,
                  disabled: true,
                },
              },
              {
                className: "col-12 col-md-4 col-lg-2",
                type: "input",
                key: "totalRow",
                templateOptions: {
                  type: "number",
                  label: this.translateService.instant(
                    "balances.fields.detail.totalRow"
                  ),
                  placeholder: this.translateService.instant(
                    "balances.fields.detail.totalRow"
                  ),
                  readonly: true,
                  min: 0,
                  disabled: true,
                },
              },
            ],
          },
        },
      ];
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   */
   onClose(): void {
    this.dialogRef.close();
  }
}
